import * as React from "react";
import { cn } from "~/lib/utils";
import { Button as ButtonComponent } from "../ui/button";

type Props = React.ComponentProps<typeof ButtonComponent>;

export function Button({
  className,
  variant,
  ...props
}: Omit<Props, "variant"> & {
  variant?:
    | "primary-filled-lg"
    | "primary-filled-sm"
    | "primary-outline-lg"
    | "primary-outline-sm"
    | "primary-ghost-lg"
    | "primary-ghost-sm"
    | "primary-link-lg"
    | "primary-link-sm"
    | "black-filled-lg"
    | "black-filled-sm"
    | "black-outline-lg"
    | "black-outline-sm"
    | "black-ghost-lg"
    | "black-ghost-sm"
    | "black-link-lg"
    | "black-link-sm"
    | "gray-filled-lg"
    | "gray-filled-sm"
    | "gray-outline-lg"
    | "gray-outline-sm"
    | "gray-ghost-lg"
    | "gray-ghost-sm"
    | "gray-link-lg"
    | "gray-link-sm"
    | "secondary-green-filled-lg"
    | "secondary-green-filled-sm"
    | "secondary-green-outline-lg"
    | "secondary-green-outline-sm"
    | "secondary-green-ghost-lg"
    | "secondary-green-ghost-sm"
    | "secondary-green-link-lg"
    | "secondary-green-link-sm";
}) {
  let accentColor: string = "var(--color-primary-500)";
  let bgColor: string = "transparent";
  let borderColor: string = "transparent";
  //
  // filled
  //
  if (variant?.startsWith("primary-filled"))
    accentColor =
      "text-[var(--color-primary-950)] hover:text-[var(--color-primary-950)]";
  if (variant?.startsWith("primary-filled"))
    bgColor =
      "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-600)]";

  if (variant?.startsWith("black-filled"))
    accentColor = "text-[var(--color-white)] hover:text-[var(--color-white)]";
  if (variant?.startsWith("black-filled"))
    bgColor = "bg-[var(--color-gray-900)] hover:bg-[var(--color-gray-950)]";

  if (variant?.startsWith("gray-filled"))
    accentColor = "text-[var(--color-white)] hover:text-[var(--color-white)]";
  if (variant?.startsWith("gray-filled"))
    bgColor = "bg-[var(--color-gray-700)] hover:bg-[var(--color-gray-800)]";

  if (variant?.startsWith("secondary-green-filled"))
    accentColor = "text-[var(--color-white)] hover:text-[var(--color-white)]";
  if (variant?.startsWith("secondary-green-filled"))
    bgColor =
      "bg-[var(--color-secondary-700)] hover:bg-[var(--color-secondary-800)]";

  //
  // outline
  //
  if (variant?.startsWith("primary-outline"))
    accentColor =
      "text-[var(--color-primary-500)] hover:text-[var(--color-primary-700)]";
  if (variant?.startsWith("primary-outline")) bgColor = "bg-transparent";
  if (variant?.startsWith("primary-outline"))
    borderColor =
      "border-[var(--color-primary-500)] hover:border-[var(--color-primary-700)]";

  if (variant?.startsWith("black-outline"))
    accentColor =
      "text-[var(--color-gray-900)] hover:text-[var(--color-gray-950)]";
  if (variant?.startsWith("black-outline")) bgColor = "bg-transparent";
  if (variant?.startsWith("black-outline"))
    borderColor =
      "border-[var(--color-gray-900)] hover:border-[var(--color-gray-950)]";

  if (variant?.startsWith("gray-outline"))
    accentColor =
      "text-[var(--color-gray-600)] hover:text-[var(--color-gray-700)]";
  if (variant?.startsWith("gray-outline")) bgColor = "bg-transparent";
  if (variant?.startsWith("gray-outline"))
    borderColor =
      "border-[var(--color-gray-600)] hover:border-[var(--color-gray-700)]";

  if (variant?.startsWith("secondary-green-outline"))
    accentColor =
      "text-[var(--color-secondary-700)] hover:text-[var(--color-secondary-800)]";
  if (variant?.startsWith("secondary-green-outline"))
    bgColor = "bg-transparent";
  if (variant?.startsWith("secondary-green-outline"))
    borderColor =
      "border-[var(--color-secondary-700)] hover:border-[var(--color-secondary-800)]";

  //
  // ghost
  //
  if (variant?.startsWith("primary-ghost"))
    accentColor =
      "text-[var(--color-primary-800)] hover:text-[var(--color-primary-900)]";
  if (variant?.startsWith("primary-ghost"))
    bgColor =
      "bg-[var(--color-primary-200)] hover:bg-[var(--color-primary-100)]";

  if (variant?.startsWith("black-ghost"))
    accentColor =
      "text-[var(--color-gray-900)] hover:text-[var(--color-gray-950)]";
  if (variant?.startsWith("black-ghost"))
    bgColor = "bg-[var(--color-gray-900)] hover:bg-[var(--color-gray-950)]";

  if (variant?.startsWith("gray-ghost"))
    accentColor =
      "text-[var(--color-gray-800)] hover:text-[var(--color-gray-900)]";
  if (variant?.startsWith("gray-ghost"))
    bgColor = "bg-[var(--color-gray-300)] hover:bg-[var(--color-gray-200)]";

  if (variant?.startsWith("secondary-green-ghost"))
    accentColor =
      "text-[var(--color-secondary-700)] hover:text-[var(--color-secondary-800)]";
  if (variant?.startsWith("secondary-green-ghost")) bgColor = "bg-transparent";

  //
  // link (just text)
  //
  if (variant?.startsWith("primary-link"))
    accentColor =
      "text-[var(--color-primary-500)] hover:text-[var(--color-primary-700)]";

  if (variant?.startsWith("black-link"))
    accentColor =
      "text-[var(--color-gray-900)] hover:text-[var(--color-gray-950)]";

  if (variant?.startsWith("gray-link"))
    accentColor =
      "text-[var(--color-gray-700)] hover:text-[var(--color-gray-800)]";

  if (variant?.startsWith("secondary-green-link"))
    accentColor =
      "text-[var(--color-secondary-700)] hover:text-[var(--color-secondary-800)]";

  let size: "sm" | "lg" = "sm";
  if (variant?.endsWith("sm")) size = "sm";
  if (variant?.endsWith("lg")) size = "lg";

  return (
    <ButtonComponent
      className={cn(
        "rounded-full cursor-pointer  uppercase font-medium font-sans tracking-wider text-white border-2 border-transparent bg-transparent hover:bg-transparent shadow-none",
        // variant === "filled" && "bg-[var(--accent-color)] hover:bg-[var(--accent-color-hover)]",
        // variant === "outline" &&
        //   "bg-transparent hover:bg-[var(--accent-color-hover)]/10 text-[var(--accent-color)] border-2 border-[var(--accent-color)] hover:border-[var(--accent-color-hover)] hover:text-[var(--accent-color-hover)]",
        // size
        // size === "lg" && "text-sm py-3 h-auto px-6 has-[>svg]:px-5",
        size === "sm" && "text-xs py-2 h-auto px-4 has-[>svg]:px-3",
        size === "lg" && "text-sm py-3 h-auto px-6 has-[>svg]:px-5",
        accentColor,
        bgColor,
        borderColor,
        className
      )}
      style={
        {
          // "--accent-color": accentColor,
          // "--accent-color-hover": accentColor,
          // "--bg-color": bgColor,
          // "--border-color": borderColor,
        } as React.CSSProperties
      }
      {...props}
    />
  );
}
